import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useDataStore } from "../dataStore";
import { toMediaUrl, withImageSize } from "../api";
import { brandThemeCssVars } from "../themeTokens";
import PageStateCard from "../components/PageStateCard";
import BrandedImageFallback from "../components/BrandedImageFallback";

function getMainImage(product) {
  const imgs = product?.productImage_product || [];
  const main = imgs.find((x) => x.isMain) || imgs[0];
  return main?.image ? toMediaUrl(main.image) : "";
}

const safeStr = (v) => (v ?? "").toString();

const byName = (a, b) =>
  safeStr(a).localeCompare(safeStr(b), "ro", { sensitivity: "base" });

const byPriorityThenName = (a, b) => {
  const ap = Number.isFinite(+a?.priority) ? +a.priority : 999999;
  const bp = Number.isFinite(+b?.priority) ? +b.priority : 999999;

  if (ap !== bp) return ap - bp;

  return byName(a?.shortName || a?.name, b?.shortName || b?.name);
};

function ProductGridSkeleton() {
  return (
    <Grid container spacing={{ xs: 1.5, md: 2 }}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              overflow: "hidden",
              borderRadius: "8px",
              border: "1px solid rgba(124,45,18,0.08)",
              bgcolor: "rgba(255,255,255,0.88)",
              boxShadow: "0 12px 30px rgba(33,20,12,0.07)",
            }}
          >
            <Skeleton
              variant="rounded"
              height={220}
              sx={{
                borderRadius: 0,
                bgcolor: "rgba(254,215,170,0.34)",
              }}
            />

            <CardContent sx={{ p: 1.75 }}>
              <Stack spacing={1}>
                <Skeleton width="76%" height={25} />
                <Skeleton width="94%" />
                <Skeleton width="58%" />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                  <Skeleton variant="rounded" width={96} height={28} sx={{ borderRadius: "8px" }} />
                  <Skeleton variant="rounded" width={30} height={30} sx={{ borderRadius: "8px" }} />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export default function Products() {
  const {
    loading,
    error,
    products,
    categories,
    getCategoryName,
    brandTheme,
    t,
    retryCore,
  } = useDataStore();
  const c = brandTheme;
  const location = useLocation();
  const compactViewport = useMediaQuery("(max-width:899.95px)");

  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("ALL");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [heroRandomSeed] = useState(() => Math.random());

  const { orderedCategories, subPriorityByIref, subNameByIref } = useMemo(() => {
    const cats = (categories || [])
      .filter((c) => c.isForSale !== false)
      .slice()
      .sort(byPriorityThenName);

    const subPrio = new Map();
    const subName = new Map();

    cats.forEach((c) => {
      (c.subCategory || [])
        .slice()
        .sort(byPriorityThenName)
        .forEach((sc) => {
          subPrio.set(
            String(sc.iref),
            Number.isFinite(+sc.priority) ? +sc.priority : 999999
          );

          subName.set(String(sc.iref), sc.shortName || sc.name || "");
        });
    });

    return {
      orderedCategories: cats,
      subPriorityByIref: subPrio,
      subNameByIref: subName,
    };
  }, [categories]);

  useEffect(() => {
    const requestedCategory = new URLSearchParams(location.search).get(
      "category"
    );
    const validCategory = orderedCategories.find(
      (category) => String(category.iref) === String(requestedCategory)
    );

    setActiveCat(validCategory?.iref || "ALL");
  }, [location.search, orderedCategories]);

  const filteredSorted = useMemo(() => {
    const query = q.trim().toLowerCase();

    const catPrio = new Map(
      orderedCategories.map((c) => [
        String(c.iref),
        Number.isFinite(+c.priority) ? +c.priority : 999999,
      ])
    );

    return (products || [])
      .filter((p) =>
        activeCat === "ALL" ? true : String(p.pCategory) === String(activeCat)
      )
      .filter((p) => {
        if (!query) return true;

        return (
          safeStr(p.name).toLowerCase().includes(query) ||
          safeStr(p.shortName).toLowerCase().includes(query) ||
          safeStr(p.slug).toLowerCase().includes(query) ||
          safeStr(p.description).toLowerCase().includes(query)
        );
      })
      .slice()
      .sort((a, b) => {
        const aCat = catPrio.get(String(a.pCategory)) ?? 999999;
        const bCat = catPrio.get(String(b.pCategory)) ?? 999999;

        if (aCat !== bCat) return aCat - bCat;

        const aSub =
          subPriorityByIref.get(String(a.pSubCategory)) ??
          (Number.isFinite(+a.pSubCategory_priority)
            ? +a.pSubCategory_priority
            : 999999);

        const bSub =
          subPriorityByIref.get(String(b.pSubCategory)) ??
          (Number.isFinite(+b.pSubCategory_priority)
            ? +b.pSubCategory_priority
            : 999999);

        if (aSub !== bSub) return aSub - bSub;

        return byName(a.name, b.name);
      });
  }, [products, q, activeCat, orderedCategories, subPriorityByIref]);

  const heroImage = useMemo(() => {
    const images = (products || [])
      .map(getMainImage)
      .filter(Boolean);

    if (!images.length) return "";

    const randomIndex = Math.min(
      images.length - 1,
      Math.max(0, Math.floor(heroRandomSeed * images.length))
    );
    return images[randomIndex] || "";
  }, [products, heroRandomSeed]);

  const activeCategoryLabel = useMemo(() => {
    if (activeCat === "ALL") return t("common.allCategories");

    const category = orderedCategories.find(
      (c) => String(c.iref) === String(activeCat)
    );

    return category?.shortName || category?.name || t("common.selectedCategory");
  }, [activeCat, orderedCategories, t]);

  const hasActiveFilters = q.trim() || activeCat !== "ALL";

  return (
    <Stack
      spacing={{ xs: 2.5, md: 3 }}
      sx={{
        ...brandThemeCssVars(c),
        pb: { xs: 20, sm: 15, md: 0 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          overflow: "hidden",
          borderRadius: "8px",
          position: "relative",
          background:
            "linear-gradient(135deg, rgba(255,250,243,0.98) 0%, rgba(255,237,213,0.90) 46%, rgba(245,158,11,0.24) 100%)",
          border: "1px solid rgba(124,45,18,0.12)",
          boxShadow: "0 18px 54px rgba(33,20,12,0.12)",
          "&::after": {
            content: '""',
            position: "absolute",
            left: 18,
            right: 18,
            bottom: 0,
            height: 1,
            background:
              "linear-gradient(90deg, rgba(124,45,18,0) 0%, rgba(124,45,18,0.24) 50%, rgba(124,45,18,0) 100%)",
            pointerEvents: "none",
          },
        }}
      >
        {heroImage ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${withImageSize(heroImage, "l")})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.16,
              filter: "saturate(1.08) contrast(0.96)",
              transform: "scale(1.04)",
            }}
          />
        ) : null}

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(115deg, rgba(255,255,255,0.68) 0%, rgba(255,237,213,0.48) 52%, rgba(124,45,18,0.16) 100%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(160deg, rgba(247,181,0,0.20) 0%, rgba(247,181,0,0) 34%, rgba(15,118,110,0.13) 100%)",
            clipPath: "polygon(0 0, 100% 0, 100% 64%, 0 100%)",
            pointerEvents: "none",
          }}
        />

        <Grid container sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} md={7}>
            <Stack
              spacing={1.5}
              sx={{
                p: { xs: 2.25, sm: 3, md: 3.5 },
                minHeight: { xs: 260, md: 320 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: c.onPrimary,
                    background: c.primary,
                    border: "1px solid rgba(124,45,18,0.10)",
                    boxShadow: "0 12px 26px rgba(247,181,0,0.18)",
                    flexShrink: 0,
                  }}
                >
                  <RestaurantMenuRoundedIcon />
                </Box>

                <Chip
                  label={activeCategoryLabel}
                  sx={{
                    color: c.text,
                    bgcolor: "rgba(255,250,243,0.82)",
                    border: "1px solid rgba(124,45,18,0.16)",
                    backdropFilter: "blur(14px)",
                    fontWeight: 850,
                  }}
                />
              </Stack>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 950,
                  color: c.text,
                  letterSpacing: 0,
                  lineHeight: 1.02,
                  fontSize: { xs: 36, sm: 48, md: 58 },
                  maxWidth: 620,
                }}
              >
                {t("products.heroTitle")}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(67,20,7,0.82)",
                  lineHeight: 1.65,
                  maxWidth: 560,
                  fontSize: { xs: 15, md: 17 },
                }}
              >
                {t("products.heroSubtitle")}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, pt: 0.5 }}>
                <Chip
                  icon={<LocalOfferOutlinedIcon />}
                  label={`${filteredSorted.length} ${t("products.displayed")}`}
                  sx={{
                    fontWeight: 850,
                    color: c.onPrimary,
                    bgcolor: c.primary,
                    border: "1px solid rgba(124,45,18,0.10)",
                    boxShadow: "0 10px 24px rgba(247,181,0,0.16)",
                    "& .MuiChip-icon": { color: "#111111" },
                  }}
                />

                {activeCat !== "ALL" ? (
                  <Chip
                    label={t("products.activeFilter")}
                    sx={{
                      fontWeight: 850,
                      color: c.text,
                      bgcolor: "rgba(255,250,243,0.82)",
                      border: "1px solid rgba(124,45,18,0.16)",
                      backdropFilter: "blur(14px)",
                    }}
                  />
                ) : null}
              </Stack>
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: { xs: "none", md: "block" },
              position: "relative",
              minHeight: 320,
            }}
          >
            {heroImage ? (
              <Box
                sx={{
                  position: "absolute",
                  overflow: "hidden",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.82)",
                  boxShadow: "0 24px 54px rgba(33,20,12,0.14)",
                  bgcolor: "#fff7ed",
                  width: 260,
                  height: 230,
                  right: 44,
                  top: 48,
                }}
              >
                <img
                  src={withImageSize(heroImage, "l")}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Box>

      {error ? (
        <PageStateCard
          tone="error"
          title={t("common.loadErrorTitle")}
          description={t("common.loadError")}
          actionLabel={t("common.retry")}
          onAction={retryCore}
          brandTheme={c}
        />
      ) : null}

      {/* Search + category filter */}
      {compactViewport ? (
        <Box
          data-testid="product-mobile-discovery-toolbar"
          sx={{
            order: -2,
            display: { xs: "block", md: "none" },
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1190,
            boxSizing: "border-box",
            px: { xs: 1.5, sm: 1.25 },
            pt: 1,
            pb: "calc(10px + env(safe-area-inset-bottom))",
            borderRadius: "16px 16px 0 0",
            bgcolor: "rgba(255,250,243,0.90)",
            backdropFilter: "blur(22px)",
            border: "1px solid rgba(67,20,7,0.12)",
            borderBottom: 0,
            boxShadow: "0 18px 52px rgba(33,20,12,0.16)",
          }}
        >
        <Stack spacing={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {compactViewport && !mobileSearchOpen ? (
                <Chip
                  icon={<SearchRoundedIcon />}
                  label={t("products.search")}
                  clickable
                  onClick={() => setMobileSearchOpen(true)}
                  sx={{
                    minHeight: 44,
                    fontWeight: 850,
                    bgcolor: "rgba(255,255,255,0.96)",
                    border: "1px solid rgba(67,20,7,0.12)",
                  }}
                />
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TextField
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("products.search")}
                    inputProps={{ "aria-label": t("products.search") }}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: "8px",
                        bgcolor: "rgba(255,255,255,0.96)",
                        fontWeight: 700,
                        minHeight: 44,
                      },
                    }}
                  />
                  {compactViewport ? (
                    <IconButton
                      aria-label={`${t("common.close")} ${t(
                        "products.search"
                      )}`}
                      onClick={() => {
                        setQ("");
                        setMobileSearchOpen(false);
                      }}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CloseRoundedIcon />
                    </IconButton>
                  ) : null}
                </Stack>
              )}
            </Box>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ flexShrink: 0, minWidth: 0 }}
            >
              <Chip
                label={`${filteredSorted.length} ${t("products.count")}`}
                sx={{
                  height: 36,
                  fontWeight: 900,
                  color: c.onPrimary,
                  bgcolor: c.primary,
                  border: "1px solid rgba(17,17,17,0.08)",
                }}
              />

              {hasActiveFilters ? (
                <Chip
                  icon={<CloseRoundedIcon />}
                  label={t("common.reset")}
                  clickable
                  onClick={() => {
                    setQ("");
                    setActiveCat("ALL");
                  }}
                  sx={{
                    height: 36,
                    fontWeight: 850,
                    bgcolor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(67,20,7,0.12)",
                    "& .MuiChip-icon": { color: "#9a3412" },
                  }}
                />
              ) : null}
            </Stack>
          </Stack>

          <Box
            sx={{
              mx: { xs: -1.5, sm: -0.25 },
              px: { xs: 1.5, sm: 0.25 },
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                width: "max-content",
                minWidth: "100%",
                pb: 0.15,
              }}
            >
              <Chip
                label={t("common.allCategories")}
                clickable
                onClick={() => setActiveCat("ALL")}
                aria-pressed={activeCat === "ALL"}
                sx={{
                  fontWeight: 900,
                  flexShrink: 0,
                  height: 34,
                  px: 0.4,
                  color: activeCat === "ALL" ? c.onCta : c.text,
                  bgcolor: activeCat === "ALL" ? c.cta : "rgba(255,255,255,0.78)",
                  border: activeCat === "ALL"
                    ? "1px solid rgba(17,17,17,0.08)"
                    : "1px solid rgba(67,20,7,0.12)",
                }}
              />

              {orderedCategories.map((category) => {
                const active = String(activeCat) === String(category.iref);

                return (
                  <Chip
                    key={category.iref}
                    label={category.shortName || category.name}
                    clickable
                    onClick={() => setActiveCat(category.iref)}
                    aria-pressed={active}
                    sx={{
                      fontWeight: 900,
                      flexShrink: 0,
                      height: 34,
                      px: 0.4,
                      color: active ? c.onCta : c.text,
                      bgcolor: active ? c.cta : "rgba(255,255,255,0.78)",
                      border: active
                        ? "1px solid rgba(17,17,17,0.08)"
                        : "1px solid rgba(67,20,7,0.12)",
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

        </Stack>
        </Box>
      ) : null}

      <Box
        sx={{
          display: { xs: "block", md: "grid" },
          gridTemplateColumns: { md: "300px minmax(0, 1fr)" },
          gap: { md: 2 },
          alignItems: "start",
        }}
      >
        {!compactViewport ? (
          <Box
            data-testid="product-discovery-toolbar"
            sx={{
              position: "sticky",
              top: 96,
              borderRadius: "8px",
              border: "1px solid rgba(67,20,7,0.12)",
              bgcolor: "rgba(255,250,243,0.88)",
              backdropFilter: "blur(18px)",
              boxShadow: "0 16px 44px rgba(33,20,12,0.10)",
              p: 1.5,
            }}
          >
            <Stack spacing={1.25}>
            <Typography sx={{ fontWeight: 950, color: "#431407", lineHeight: 1.1 }}>
              {t("products.explore")}
            </Typography>

            <TextField
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("products.search")}
              inputProps={{ "aria-label": t("products.search") }}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "8px",
                  bgcolor: "rgba(255,255,255,0.96)",
                  fontWeight: 700,
                },
              }}
            />

            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip
                label={`${filteredSorted.length} ${t("products.count")}`}
                sx={{
                  height: 34,
                  fontWeight: 900,
                  color: c.onPrimary,
                  bgcolor: c.primary,
                  border: "1px solid rgba(17,17,17,0.08)",
                }}
              />

              {hasActiveFilters ? (
                <Chip
                  icon={<CloseRoundedIcon />}
                  label={t("common.reset")}
                  clickable
                  onClick={() => {
                    setQ("");
                    setActiveCat("ALL");
                  }}
                  sx={{
                    height: 34,
                    fontWeight: 850,
                    bgcolor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(67,20,7,0.12)",
                    "& .MuiChip-icon": { color: "#9a3412" },
                  }}
                />
              ) : null}
            </Stack>

            <Stack spacing={0.75}>
              <Chip
                label={t("common.allCategories")}
                clickable
                onClick={() => setActiveCat("ALL")}
                aria-pressed={activeCat === "ALL"}
                sx={{
                  justifyContent: "flex-start",
                  height: 36,
                  fontWeight: 900,
                  color: activeCat === "ALL" ? c.onCta : c.text,
                  bgcolor: activeCat === "ALL" ? c.cta : "rgba(255,255,255,0.78)",
                  border: activeCat === "ALL"
                    ? "1px solid rgba(17,17,17,0.08)"
                    : "1px solid rgba(67,20,7,0.12)",
                }}
              />

              {orderedCategories.map((category) => {
                const active = String(activeCat) === String(category.iref);

                return (
                  <Chip
                    key={category.iref}
                    label={category.shortName || category.name}
                    clickable
                    onClick={() => setActiveCat(category.iref)}
                    aria-pressed={active}
                    sx={{
                      justifyContent: "flex-start",
                      height: 36,
                      fontWeight: 900,
                      color: active ? c.onCta : c.text,
                      bgcolor: active ? c.cta : "rgba(255,255,255,0.78)",
                      border: active
                        ? "1px solid rgba(17,17,17,0.08)"
                        : "1px solid rgba(67,20,7,0.12)",
                    }}
                  />
                );
              })}
            </Stack>
            </Stack>
          </Box>
        ) : null}

        <Box sx={{ minWidth: 0 }}>
          {/* Empty state */}
          {!loading && !error && !filteredSorted.length ? (
            <PageStateCard
              title={t("products.emptyTitle")}
              description={t("products.emptyDesc")}
              actionLabel={hasActiveFilters ? t("products.resetFilters") : ""}
              onAction={hasActiveFilters ? () => {
                setQ("");
                setActiveCat("ALL");
              } : undefined}
              brandTheme={c}
            />
          ) : null}

          {/* Product grid */}
          {loading ? (
            <ProductGridSkeleton />
          ) : !error ? (
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
        {filteredSorted.map((p) => {
          const img = getMainImage(p);
          const catLabel = getCategoryName(p.pCategory);
          const subLabel = subNameByIref.get(String(p.pSubCategory)) || "";
          const detailText =
            safeStr(p.description).trim() ||
            subLabel ||
            p.shortName ||
            t("common.productDetails");

          return (
            <Grid item xs={12} sm={6} md={4} key={p.iref}>
              <Card
                component={RouterLink}
                to={`/products/${p.slug || p.iref}`}
                elevation={0}
                sx={{
                  height: "100%",
                  textDecoration: "none",
                  color: "inherit",
                  overflow: "hidden",
                  borderRadius: "8px",
                  border: "1px solid rgba(124,45,18,0.08)",
                  bgcolor: "rgba(255,255,255,0.92)",
                  boxShadow: "0 12px 30px rgba(33,20,12,0.08)",
                  transition:
                    "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 20px 46px rgba(33,20,12,0.14)",
                    borderColor: "rgba(234,88,12,0.24)",
                  },
                  "&:focus-visible": {
                    outline: "3px solid rgba(247,181,0,0.34)",
                    outlineOffset: 3,
                  },
                  "&:hover .premium-card-media": {
                    transform: "scale(1.035)",
                    filter: "saturate(1.06)",
                  },
                  "&:hover .premium-card-arrow": {
                    transform: "translateX(3px)",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    aspectRatio: "4 / 3",
                    overflow: "hidden",
                    bgcolor: "#fff7ed",
                  }}
                >
                  {img ? (
                    <Box
                      component="img"
                      className="premium-card-media"
                      src={withImageSize(img, "m")}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 220ms ease, filter 220ms ease",
                      }}
                    />
                  ) : (
                    <BrandedImageFallback
                      className="premium-card-media"
                      icon={<RestaurantMenuRoundedIcon />}
                      label={t("common.noImage")}
                      brandTheme={c}
                      sx={{ transition: "transform 220ms ease, filter 220ms ease" }}
                    />
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, rgba(17,17,17,0.02) 40%, rgba(17,17,17,0.42) 100%)",
                      pointerEvents: "none",
                    }}
                  />

                  {catLabel ? (
                    <Chip
                      size="small"
                      label={catLabel}
                      sx={{
                        position: "absolute",
                        left: 10,
                        bottom: 10,
                        maxWidth: "calc(100% - 20px)",
                        color: "#111111",
                        bgcolor: "rgba(255,255,255,0.92)",
                        border: "1px solid rgba(255,255,255,0.36)",
                        fontWeight: 850,
                      }}
                    />
                  ) : null}

                  {p.isNew ? (
                    <Chip
                      size="small"
                      label={t("products.newShort")}
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: 10,
                        color: c.onPrimary,
                        bgcolor: c.primary,
                        border: "1px solid rgba(17,17,17,0.08)",
                        boxShadow: "0 10px 22px rgba(33,20,12,0.14)",
                        fontWeight: 900,
                      }}
                    />
                  ) : null}
                </Box>

                <CardContent sx={{ p: 1.75 }}>
                  <Stack spacing={0.9}>
                    <Typography
                      sx={{
                        fontWeight: 950,
                        color: c.text,
                        lineHeight: 1.22,
                        fontSize: { xs: 16.5, md: 17 },
                      }}
                    >
                      {p.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.45,
                        minHeight: 40,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {detailText}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ pt: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={t("common.viewDetails")}
                        sx={{
                          fontWeight: 800,
                          bgcolor: "#fff8e1",
                          color: c.accent,
                          border: "1px solid rgba(124,45,18,0.10)",
                        }}
                      />

                      <Box
                        className="premium-card-arrow"
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: c.accent,
                          bgcolor: "rgba(255,247,237,0.72)",
                          border: "1px solid rgba(124,45,18,0.10)",
                          transition: "transform 160ms ease",
                        }}
                      >
                        <ArrowForwardRoundedIcon fontSize="small" />
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
          </Grid>
          ) : null}
        </Box>
      </Box>
    </Stack>
  );
}
